/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {GENERATE_EVENTS_VALUE} from '@ion/dls/src/maps/geofences/constants';
import {SavedPlacesProps} from '@ion/dls/src/maps/geofences/SearchAddress';
import {BoundaryMode, CreateGeoFenceTypeEnum, TimeModes, TrackingMode} from '@ion/dls/src/types/types';
import Features, {ADMIN_ALERTS_PAGE} from '@/security/Features';
import {setBoundaryDrawingTool, setBoundaryPanelOpen} from '@/store/actions/reports';
import {
  saveGeofence,
  setBoundaryFilterMode,
  setContextMenuVisibility,
  setLiveHistoryUIFetching,
  setLocationPopupFindNearby,
  setSearchHistory,
  toggleMapControls
} from '@/store/actions/tracking';
import {
  assetCategoriesSelector,
  contextMenuLocationSelector,
  contextMenuVisibilitySelector,
  geofenceStatusSelector,
  historyActiveSelector,
  historySearchSelector,
  liveHistoryActiveSelector,
  mapBasemapSelector,
  mapLayerTypeSelector,
  savedGeofenceSelector,
  savedMapViewsSelector,
  searchResultSelector,
  trackingModeSelector
} from '@/store/selectors/tracking-selectors';
import {useReturnToLiveTracking} from '../../../../tracking/hooks/history/useReturnToLiveTracking';
import useBottomLeftToolProps from './useBottomLeftToolProps';
import useBottomSheetProps from './useBottomSheetProps';
import useBoundaryPanelProps from './useBoundaryPanelProps';
import useLayerChooserProps from './useLayerChooserProps';
import useOperationalLayerProps from './useOperationalLayerProps';
import useTimeModeProps from './useTimeModeProps';

/**
 * The useMapToolProps hook is used for sharing props to Map Tools components.
 * 
 * ```typescript
 * 
 *   import useMapToolProps from './hooks/useMapToolProps';
 * 
 *   const mapToolProps = useMapToolProps();
 * 
 *   // mapToolProps
 * 
 * ```
 */
export const useMapToolProps = () => {
  const dispatch              = useDispatch();
  const isGeofenceCreatedRef  = useRef(false);
  const basemap               = useSelector(mapBasemapSelector);
  const geofenceStatus        = useSelector(geofenceStatusSelector);
  const searchHistory         = useSelector(historySearchSelector);
  const savedGeofence         = useSelector(savedGeofenceSelector);
  const mapLayerType          = useSelector(mapLayerTypeSelector);
  const searchResult          = useSelector(searchResultSelector);
  const trackingMode          = useSelector(trackingModeSelector);
  const savedMapViews         = useSelector(savedMapViewsSelector);
  const assetCategories       = useSelector(assetCategoriesSelector);
  const bottomLeftToolProps   = useBottomLeftToolProps();
  const bottomSheetProps      = useBottomSheetProps();
  const operationalLayerProps = useOperationalLayerProps();
  const boundaryPanelProps    = useBoundaryPanelProps();
  const layerChooserProps     = useLayerChooserProps();
  const contextMenuLocation   = useSelector(contextMenuLocationSelector);
  const contextMenuVisibility = useSelector(contextMenuVisibilitySelector);
  const shouldHideFindNearby  = trackingMode === TrackingMode.History;

  const {
    onBaseMapChange,
    onSaveMapView,
    editSavedMapView,
    setDefaultMapView,
    deleteSavedMapView
  } = bottomSheetProps;

  const {onTimeModeChange: setTimeModeDirect} = useTimeModeProps();
  const returnToLiveTracking = useReturnToLiveTracking();
  const isLiveHistoryActive  = useSelector(liveHistoryActiveSelector);
  const isHistoryActive      = useSelector(historyActiveSelector);

  // the control's own Live pill moves the mode the same way the applied
  // filter bar's chips and Clear All do - selecting Timeline/History still
  // just sets the mode, but selecting Live tears whichever of the two was
  // running all the way down instead of only switching the control's own
  // reading of it ...
  const onTimeModeChange = useCallback((mode: TimeModes) => {
    if (mode === TimeModes.live) {
      returnToLiveTracking();
    } else {
      setTimeModeDirect(mode);
    }
  }, [returnToLiveTracking, setTimeModeDirect]);

  // the pill only lights up once a selection is actually applied - opening a
  // mode's panel moves redux's own `timeMode` (which panel is on screen)
  // right away, but the control's own reading here keeps pointing at
  // whichever mode last ran until Apply confirms the new one. Read off the
  // same flags the applied filter bar's own Timeline/History chips use, so
  // both answer the same question the same way rather than tracking it
  // separately - `trackingMode` is checked alongside each flag too, the same
  // reasoning the chips themselves check it under ...
  const timeMode = isLiveHistoryActive && trackingMode === TrackingMode.Live
    ? TimeModes.timeline
    : isHistoryActive && trackingMode === TrackingMode.History
      ? TimeModes.history
      : TimeModes.live;

  useEffect(() => {
    if (geofenceStatus?.isCreate &&
      Features.hasFeature(ADMIN_ALERTS_PAGE) &&
      geofenceStatus?.success &&
      isGeofenceCreatedRef.current &&
      savedGeofence?.geofenceType === GENERATE_EVENTS_VALUE) {
      isGeofenceCreatedRef.current = false;
    }
  }, [geofenceStatus, savedGeofence]);

  const closeMapControlsSheet = useCallback(function() {
    dispatch(toggleMapControls(false));
  }, []);

  const saveSearchHistory = useCallback(function(location: SavedPlacesProps) {
    dispatch(setSearchHistory(location));
  }, []);

  const onLocationPopupFindNearby = useCallback(function(locationCoordinates) {
    dispatch(setLocationPopupFindNearby(locationCoordinates, null) as any);
  }, []);

  const onLiveHistoryUIFetching = useCallback(fetching => dispatch(setLiveHistoryUIFetching(fetching)), []);

  const geofenceSave = useCallback(function({
    geofence,
    isUpdate = false,
    landmarkName = null,
    isDelete = false
  }) {
    let shouldShowSuccessPrompt = isUpdate || isDelete;
    if (!shouldShowSuccessPrompt) {
      const isValidFeature    = !Features.hasFeature(ADMIN_ALERTS_PAGE);
      const isValidEvent      = geofence?.attrs?.geofenceType !== GENERATE_EVENTS_VALUE;
      shouldShowSuccessPrompt = isValidFeature || isValidEvent;
    }
    isGeofenceCreatedRef.current = !isUpdate && !isDelete;
    dispatch(saveGeofence({
      isUpdate,
      landmarkName,
      geofence,
      isDelete,
      shouldShowSuccessPrompt
    }) as any);
    closeMapControlsSheet();
  }, []);

  const onVicinitySearch = useCallback(() => {
    dispatch(setContextMenuVisibility(false));
    dispatch(setBoundaryFilterMode(BoundaryMode.Vicinity));
    dispatch(setBoundaryDrawingTool(CreateGeoFenceTypeEnum.Radial));
    dispatch(setBoundaryPanelOpen(true));
  }, [contextMenuLocation]);

  const mapToolProps = useMemo(() => {
    return {
      isTracking              : true,
      useHereApi              : true,
      savedSearchHistoryItems : searchHistory,
      bottomRightToolProps    : {},
      contextMenuProps        : {
        location   : contextMenuLocation,
        visibility : contextMenuVisibility,
        onVicinitySearch
      },
      basemap,
      timeMode,
      onBaseMapChange,
      geofenceSave,
      mapLayerType,
      searchResult,
      savedMapViews,
      onSaveMapView,
      assetCategories,
      onTimeModeChange,
      editSavedMapView,
      setDefaultMapView,
      saveSearchHistory,
      onVicinitySearch,
      deleteSavedMapView,
      bottomSheetProps,
      layerChooserProps,
      boundaryPanelProps,
      bottomLeftToolProps,
      operationalLayerProps,
      onLiveHistoryUIFetching,
      onLocationPopupFindNearby,
      hideFindNearby : shouldHideFindNearby
    };
  }, [
    searchHistory,
    basemap,
    timeMode,
    geofenceSave,
    mapLayerType,
    searchResult,
    savedMapViews,
    onSaveMapView,
    assetCategories,
    editSavedMapView,
    onTimeModeChange,
    setDefaultMapView,
    saveSearchHistory,
    deleteSavedMapView,
    bottomSheetProps,
    layerChooserProps,
    boundaryPanelProps,
    bottomLeftToolProps,
    contextMenuLocation,
    contextMenuVisibility,
    operationalLayerProps,
    onLiveHistoryUIFetching,
    onLocationPopupFindNearby,
    shouldHideFindNearby
  ]);
  
  return mapToolProps;
};

export default useMapToolProps;
